import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LiveEventTokenSource } from 'types';
import { EventSchema, EventStateSchema } from 'types/schemas';
import { generateRandomString, hashUsername } from 'utils';

export type EventsSlice = {
  [eventCode: string]: EventSlice;
};

export type EventSlice = {
  session: EventSliceSession;
  meta: EventSliceMeta;
  state: EventSliceState;
};

export type EventSliceSession = {
  uid: number;
  username: string | null;
  token: string;
  tokenSource: LiveEventTokenSource;
};

export type EventSliceMeta = EventSchema & {};

export type EventSliceState = EventStateSchema & {};

const initialState: EventsSlice = {};

export const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    initSession: (state, action: PayloadAction<{ eventCode: string; session: Partial<EventSliceSession> }>) => {
      // eslint-disable-next-line no-param-reassign
      state[action.payload.eventCode] = {
        ...state[action.payload.eventCode],
        session: {
          ...state[action.payload.eventCode]?.session,
          ...action.payload.session,
          uid: hashUsername(action.payload.session.username || generateRandomString())
        }
      };
    },
    updateSession: (
      state,
      action: PayloadAction<{ eventCode: string; session: Partial<Omit<EventSliceSession, 'uid'>> }>
    ) => {
      // eslint-disable-next-line no-param-reassign
      state[action.payload.eventCode] = {
        ...state[action.payload.eventCode],
        session: {
          ...state[action.payload.eventCode]?.session,
          ...action.payload.session,
          uid: action.payload.session?.username
            ? hashUsername(action.payload.session.username)
            : state[action.payload.eventCode].session.uid
        }
      };
    },
    updateMeta: (state, action: PayloadAction<EventSchema>) => {
      // eslint-disable-next-line no-param-reassign
      state[action.payload.code] = {
        ...state[action.payload.code],
        meta: {
          ...state[action.payload.code]?.meta,
          ...action.payload
        }
      };
    },
    updateState: (state, action: PayloadAction<EventStateSchema>) => {
      const prevState = state[action.payload.eventCode].state;
      const prevStateSnapshot = JSON.stringify(prevState);
      const nextStateSnapshot = JSON.stringify(action.payload);
      const snapshotsUpdated = prevStateSnapshot !== nextStateSnapshot;

      if (snapshotsUpdated) {
        // eslint-disable-next-line no-param-reassign
        state[action.payload.eventCode].state = {
          ...state[action.payload.eventCode].state,
          ...action.payload
        };
      }
    },
    toggleHandRaisePermission: (state, action: PayloadAction<{ eventCode: string; enabled: boolean }>) => {
      // eslint-disable-next-line no-param-reassign
      state[action.payload.eventCode].state.handRaiseEnabled = action.payload.enabled;
    }
  }
});

export const eventsReducer = eventsSlice.reducer;
export const eventsActions = {
  ...eventsSlice.actions
};
